import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../hooks/useChat';
import { Send } from 'lucide-react';

interface ChatRoomProps {
    roomName: string;
}

const ChatRoom: React.FC<ChatRoomProps> = ({ roomName }) => {
    const { messages, sendMessage, currentUser } = useChat(roomName);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    // This effect smoothly scrolls to the bottom of the chat when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = () => {
        if (newMessage.trim()) {
            sendMessage(newMessage);
            setNewMessage('');
        }
    };

    return (
        <div className="w-full h-full flex flex-col bg-white">
            <header className="bg-gray-100 p-4 border-b flex-shrink-0">
                <h2 className="font-bold text-center text-gray-800">Community Chat</h2>
            </header>
            
            <main className="flex-grow p-4 overflow-y-auto bg-gray-50 space-y-4">
                {messages.map((msg, index) => {
                    const isCurrentUser = currentUser?._id === msg.authorId;
                    return (
                        <div key={index} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                            <div className={`py-2 px-3 rounded-lg max-w-md shadow-sm ${isCurrentUser ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
                                {!isCurrentUser && (
                                    <p className="font-bold text-sm mb-1 text-blue-700">{msg.authorName}</p>
                                )}
                                <p className="text-base">{msg.message}</p>
                                <p className="text-xs text-right mt-1 opacity-70">
                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </main>

            <footer className="p-3 border-t flex gap-2 bg-white flex-shrink-0">
                <input
                    type="text"
                    value={newMessage}
                    placeholder="Type a message..."
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                    className="flex-grow px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button 
                    onClick={handleSendMessage} 
                    className="bg-blue-600 text-white p-3 rounded-full font-semibold hover:bg-blue-700 transition-transform transform hover:scale-110"
                    aria-label="Send message"
                >
                    <Send size={20}/>
                </button>
            </footer>
        </div>
    );
};

export default ChatRoom;